import { useState } from "react";
import { AIToolCallRecord } from "../types/ai.types";
import ToolCallChip from "./ToolCallChip";

// ---------------------------------------------------------------------------
// ToolCallsSummary — collapses the assistant turn's tool-call chips into a
// single subtle "Used N tools" row. Click to expand and see the individual
// chips (each still inspectable via the existing ToolCallChip drilldown).
//
// Why: when the model chains many tool calls (e.g. "worst counsellor" → 9×
// count_leads), rendering every chip turns the panel into a wall of green.
// Most users only want the final narrative; power users still get full
// transparency one click away.
// ---------------------------------------------------------------------------

interface Props {
  toolCalls: AIToolCallRecord[];
  /** When true, render an inline subdued pill instead of a card. Useful
   *  while still streaming so the layout doesn't jump. */
  compact?: boolean;
}

export default function ToolCallsSummary({ toolCalls, compact }: Props) {
  const [open, setOpen] = useState(false);
  if (!toolCalls?.length) return null;

  const total = toolCalls.length;
  const errored = toolCalls.filter((t) => t.error).length;
  const running = toolCalls.filter((t) => t.error === undefined && t.result === undefined).length;

  // One-line summary: group by tool name with counts.
  const byName = new Map<string, number>();
  for (const t of toolCalls) byName.set(t.name, (byName.get(t.name) ?? 0) + 1);
  const nameSummary = Array.from(byName.entries())
    .map(([n, c]) => (c > 1 ? `${n} ×${c}` : n))
    .join(" · ");

  const statusColor =
    errored > 0 ? "#9a3412" : running > 0 ? "#9a3412" : "#15803d";
  const statusDot =
    errored > 0 ? "#ef4444" : running > 0 ? "#f97316" : "#10b981";

  return (
    <div
      style={{
        margin: "4px 0",
        fontSize: 11.5,
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          background: "transparent",
          border: "1px solid var(--color-black-3, #e5e7eb)",
          borderRadius: 10,
          padding: compact ? "4px 8px" : "6px 10px",
          cursor: "pointer",
          color: "var(--color-text-secondary, #6b7280)",
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontFamily: "inherit",
        }}
        title={open ? "Hide tool details" : "Show tool details"}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: statusDot,
            display: "inline-block",
            flexShrink: 0,
          }}
        />
        <span style={{ flex: 1, textAlign: "left", color: statusColor, fontWeight: 500 }}>
          {running > 0
            ? `Running ${running} tool${running > 1 ? "s" : ""}…`
            : errored > 0
            ? `Used ${total} tool${total > 1 ? "s" : ""} · ${errored} failed`
            : `Used ${total} tool${total > 1 ? "s" : ""}`}
        </span>
        <span
          style={{
            opacity: 0.6,
            maxWidth: 180,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {nameSummary}
        </span>
        <span
          style={{
            opacity: 0.5,
            fontSize: 10,
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 120ms ease",
            display: "inline-block",
          }}
        >
          ▸
        </span>
      </button>

      {open && (
        <div
          style={{
            marginTop: 4,
            paddingLeft: 6,
            borderLeft: "2px solid var(--color-black-3, #e5e7eb)",
          }}
        >
          {toolCalls.map((tc) => (
            <ToolCallChip key={tc.id} tc={tc} />
          ))}
        </div>
      )}
    </div>
  );
}
